import { useLayoutEffect } from 'react'

import { viewportHeightCssVar } from '@sales-center/common'

/**
 * Publishes `visualViewport.height` on `<html>` as `--sc-viewport-height`, which
 * `index.tpl.html` uses to size `body` and `#root`.
 *
 * Why this is needed instead of `100dvh`: `dvh` tracks the browser's address bar
 * but deliberately ignores the on-screen keyboard. On iOS the keyboard shrinks
 * the *layout* viewport (`innerHeight` 1078 -> 738 on iPad) while `100dvh` stays
 * at the pre-keyboard height, so the document ends up ~340px taller than the
 * visible area, becomes scrollable, and iOS scrolls it to reveal the focused
 * field — dragging the `position: fixed` toolbar and the sticky Save/Cancel
 * header clean off the top of the screen. Measured on iPad/iOS 26.5: the header
 * went from `top: 78` to `top: -262` for as long as the keyboard was open.
 *
 * See WWM-17564.
 */
export const useVisualViewportHeight = () => {
  useLayoutEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const publish = () => {
      document.documentElement.style.setProperty(
        viewportHeightCssVar,
        `${viewport.height}px`,
      )

      // The document is `overflow: hidden` and must always sit at the origin.
      // iOS can still scroll it while bringing a focused field into view, and it
      // does not always put it back, so pin it here as well.
      if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0)
    }

    publish()

    viewport.addEventListener('resize', publish)
    viewport.addEventListener('scroll', publish)

    return () => {
      viewport.removeEventListener('resize', publish)
      viewport.removeEventListener('scroll', publish)
      document.documentElement.style.removeProperty(viewportHeightCssVar)
    }
  }, [])
}
